import React from "react";
import { parseSessionIsoDateForDisplay } from "../../utils/dates";
import { ucFirst } from "../../utils/strings";
import { centsToCurrencyString } from "../../utils/numbers";
import CandybarOrderSummary from "./CandybarOrderSummary";
import ConfirmationContainer from "./Confirmation/ConfirmationContainer";
import styled from "styled-components";
import { breakpoints } from "../../utils/styles";
import BodyBlocker from "../BodyBlocker";
import "./Confirmation/Confirmation.css";

const Bottom = styled.div`
    background: var(--primary);
    bottom: 0;
    color: var(--page-bg);
    display: flex;
    justify-content: space-between;
    left: 0;
    padding: 2rem 1rem 3rem;
    position: fixed;
    right: 0;
    z-index: 1032;

    & > div {
        display: flex;
    }

    body.theme-market &,
    body.theme-platinum & {
        font-size: 1rem;
        color: var(--text-primary);
    }
        
    @media screen and (min-width: ${breakpoints.desktop}px) {
        display: none;
    }
`;

const BottomTotal = styled.div`
    font-size: 1.5em;
    line-height: 1;
    font-weight: 700;
`;

const NextBtn = styled.span`
    font-size: 2em;
    margin-left: 0.5em;
`;

const CloseBtn = styled.button`
    background: transparent;
    border: none;
    color: var(--page-bg);
    margin: 0 0 2rem;
    outline: none;
    padding: 0;

    &:focus { outline: none; }

    body.theme-platinum &,
    body.theme-market & {
        color: var(--text-primary);
    }
`;

const CloseIcon = styled.span`
    border: 1px solid var(--page-bg);
    border-radius: 50%;
    display: inline-block;
    height: 2rem;
    line-height: 2rem;
    margin-right: 1em;
    text-align: center;
    width: 2rem;

    body.theme-platinum &,
    body.theme-market & {
        border-color: var(--text-primary);
    }
`;

const Head = styled.div`
    margin-bottom: 1rem;
`;

const MovieTitle = styled.p`
    color: var(--local-accent);
    line-height: 1.2;
    margin-bottom: 0;

    body.theme-market & {
        color: var(--text-primary);
        font-weight: 900;
    }
`;

const Container = styled.div<{ isOpen: boolean }>`
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
    height: 98dvh;
    position: fixed;
    bottom: ${props => props.isOpen ? '0' : '-100vh'};
    left: 0;
    right: 0;
    z-index: 10001;
    transition: bottom 0.3s ease;
    padding: var(--pad-y);

    background: var(--accent);
    color: var(--page-bg);

    body.theme-platinum & {
        color: var(--text-primary);
    }

    body.theme-market & {
        background: var(--primary);
        font-size: 1.2em;
    }

    @media screen and (min-width: ${breakpoints.desktop}px) {
        display: none;
    }
`

const CrossOverText = styled.p`
    position: absolute;
    top: 45%;
    left: 0;
    right: 0;
    text-align: center;
    transform: rotate(-10deg);
    opacity: 0.2;
    text-transform: uppercase;
    font-size: 4rem;
    font-family: var(--ff-title);

    body.theme-platinum & {
        font-size: 2rem;
    }

    body.theme-market & {
        color: var(--text-primary);
        font-size: 2em;
    }
`;

const Disclaimer = styled.p`
    background: var(--primary);
    line-height: 1.2;
    margin-bottom: 2rem;
    padding: 1rem;
    padding-left: 3.5rem;
    position: relative;

    body.theme-market & {
        background: var(--page-bg);
        color: var(--text-primary);
        font-family: var(--ff-title);
        line-height: 1.5;
    }
`;

const DisclaimerIcon = styled.span`
    font-size: 1.5rem;
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
`;

interface Props {
    session: any,
    movie: any,
    candybarAmount: number,
    candybarCatalog: { [key: string]: any } | { [key: number]: any },
    candybarOrder: any[],
    cinema: any,
    grandTotal: number,
    grandTotalPaymentString: string,
    nextButtonText: string,
    seatsLabels: string[],
    ticketsAmount: number,
    ticketsString: string,
    ticketsSum: number,
    bookingFee: number,
    onNext: () => void
};

function CheckoutMobileSummary(props: Props) {
    const [isOpen, setOpen] = React.useState(false);
    const { candybarAmount, candybarOrder, cinema, grandTotal, grandTotalPaymentString, movie, nextButtonText, session, ticketsAmount, ticketsString, ticketsSum, seatsLabels, bookingFee } = props;
    const candybarSummary = CandybarOrderSummary({
        candybarOrder: props.candybarOrder,
        candybarCatalog: props.candybarCatalog
    });

    if (!grandTotal && !ticketsSum) return null;

    return <>
        <Bottom>
            <div onClick={() => setOpen(true)}>
                <CloseIcon><i className="fal fa-angle-up"></i></CloseIcon>
                <div>
                    <BottomTotal>{centsToCurrencyString(grandTotal)}*</BottomTotal>
                    ver detalle
                </div>
            </div>
            <div onClick={props.onNext}>
                {nextButtonText}
                <NextBtn className="fal fa-arrow-right"></NextBtn>
            </div>
        </Bottom>
        {isOpen && <BodyBlocker/>}
        <Container isOpen={isOpen}>
            <div>
                <CloseBtn onClick={() => setOpen(false)}>
                    <CloseIcon><i className="fal fa-angle-down"></i></CloseIcon>
                    Ocultar detalle
                </CloseBtn>

                
                <ConfirmationContainer>
                    
                    <Head className="confirmation-info-block">
                        {cinema && <p>{cinema.name}</p>}
                        {movie && <MovieTitle className='title-1'>{movie.name}</MovieTitle>}
                    </Head>

                    <Disclaimer>
                        <DisclaimerIcon className="fas fa-exclamation-circle" style={{ marginRight: '1rem' }}></DisclaimerIcon>
                        Esta información <strong>NO</strong> es válida como boleto. Transacción pendiente de pago.
                    </Disclaimer>
                    
                    <div style={{position: 'relative'}}>
                        <div>
                            {session && (() => {
                                const date = parseSessionIsoDateForDisplay(session.datetime);

                                return <div className="confirmation-info-block">
                                    <p className="confirmation-info-row">
                                        <span className="confirmation-info-label">Fecha</span>
                                        {ucFirst(date.format('dddd D [de] MMMM'))}
                                        </p>

                                    <p className="confirmation-info-row">
                                        <span className="confirmation-info-label">Hora</span>
                                        {date.format('hh:mm A')}
                                    </p>

                                    <p className="confirmation-info-row">
                                        <span className="confirmation-info-label">Sala</span>
                                        {session.auditorium_name || session.screen_number}
                                    </p>

                                    {seatsLabels && seatsLabels.length > 0 && <p className="confirmation-info-row">
                                        <span className="confirmation-info-label">Asientos</span>
                                        {seatsLabels.join(', ')}
                                    </p>}

                                    {movie.label && movie.label !== '' && <p className="confirmation-info-row">
                                        <span className="confirmation-info-label">Formato</span>
                                        {movie.label}
                                    </p>}

                                    {ticketsString && <p className="confirmation-info-row">
                                        <span className="confirmation-info-label">Tickets</span>
                                        {ticketsString}
                                    </p>}
                                </div>;
                            })()}

                            {candybarSummary && <div className="confirmation-info-block">
                                <p className="confirmation-info-row">
                                    <span className="confirmation-info-label">Dulcería</span>
                                    {candybarSummary}
                                </p>
                            </div>}

                            {grandTotalPaymentString && <div className="confirmation-info-block">
                                {ticketsAmount > 0 && <p className="confirmation-info-row">
                                    <span className="confirmation-info-label">Tickets</span>
                                    {centsToCurrencyString(ticketsAmount)}
                                </p>}

                                {candybarAmount > 0 && <p className="confirmation-info-row">
                                    <span className="confirmation-info-label">Dulcería</span>
                                    {centsToCurrencyString(candybarAmount)}
                                </p>}

                                {bookingFee > 0 && <p className="confirmation-info-row">
                                    <span className="confirmation-info-label">Costo por servicio</span>
                                    {centsToCurrencyString(bookingFee)}
                                </p>}

                                <p className="confirmation-info-row">
                                    <span className="confirmation-info-label">Total</span>
                                    {grandTotalPaymentString}
                                </p>
                            </div>}
                        </div>
                        <CrossOverText>No válido como boleto</CrossOverText>
                    </div>
                </ConfirmationContainer>
            </div>
        </Container>
    </>;
}

export default CheckoutMobileSummary;